<script setup lang="ts">
    import { useToastStore } from '@/stores/toast.store';
    import axios from 'axios';
    import moment from 'moment';
    import { inject, onMounted, reactive, Ref, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';

    const router = useRouter();
    const route = useRoute();
    const toastStore = useToastStore();
    const errorMessage = ref('');
    const loading = ref(false);
    const collectivite = inject<Ref<any>>('collectivite');
    const startupFormDatas = reactive({
        pays: [] as any[],
        users: [] as any[]
    });
    const BACKEND_URL = inject('BACKEND_URL');
    const logoPreview = ref<string | null>(null);
    const existingLogo = ref<string | null>(null);
    const formData = reactive({
        id: null as number | null,
        nom: "",
        rccm: "",
        adresse: "",
        telephone: "",
        email: "",
        date_creation: "",
        pays_id: null as number | null,
        secteurs: [] as string[],
        gerant_id: null as number | null, // ID de l'utilisateur existant à associer
        logo: null as File | null
    });
    const errors = reactive({
        nom: "",
        rccm: "",
        adresse: "",
        telephone: "",
        email: "",
        date_creation: "",
        pays_id: "",
        secteurs: ""
    });
    const secteurs = ref([
      { id: 'finance', value: 'Finance', label: 'Finance' },
      { id: 'informatique', value: 'Informatique', label: 'Informatique' },
      { id: 'agriculture', value: 'Agriculture', label: 'Agriculture' },
      { id: 'education', value: 'Éducation', label: 'Éducation' },
      { id: 'sante', value: 'Santé', label: 'Santé' },
      { id: 'transport', value: 'Transport', label: 'Transport' },
      { id: 'construction', value: 'Construction', label: 'Construction' },
      { id: 'commerce', value: 'Commerce', label: 'Commerce' },
      { id: 'industrie', value: 'Industrie', label: 'Industrie' },
      { id: 'autre', value: 'Autre', label: 'Autre' }
    ]);

    onMounted(() => {
        const form_datas = route.meta.form_datas as { collectivite: any, startupFormDatas: any };
        Object.assign(startupFormDatas, form_datas.startupFormDatas);
        if (form_datas.collectivite) {
            const collectivite = form_datas.collectivite as any;
            if (collectivite.date_creation) {
                const date_creation = moment(collectivite.date_creation);
                collectivite.date_creation = date_creation.isValid() ? date_creation.format('YYYY-MM-DD') : '';
            }
            Object.assign(formData, collectivite);
            formData.secteurs = normalizeSecteurs(collectivite.secteurs);
            existingLogo.value = collectivite.logo || null;
        }
    });

    function normalizeSecteurs(raw: any): string[] {
        if (!Array.isArray(raw)) return [];
        const canonical = new Map(secteurs.value.map((s: any) => [String(s.value).toLowerCase(), s.value]));
        return raw.map((v: any) => {
            const key = String(v).toLowerCase();
            return canonical.get(key) ?? String(v);
        });
    }

    function onLogoSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        formData.logo = file;
        const reader = new FileReader();
        reader.onload = (e) => { logoPreview.value = e.target?.result as string; };
        reader.readAsDataURL(file);
    }

    function clearLogo() {
        formData.logo = null;
        logoPreview.value = null;
    }

    function validate() {
        let isValid = true;
        errors.nom = '';
        if (!formData.nom) {
            errors.nom = "Le nom est requis";
            isValid = false;
        }

        errors.pays_id = '';
        if (!formData.pays_id || formData.pays_id <= 0) {
            errors.pays_id = "Le pays est requis";
            isValid = false;
        }

        errors.rccm = '';
        if (!formData.rccm) {
            errors.rccm = "Le RCCM est requis";
            isValid = false;
        }

        errors.adresse = '';
        if (!formData.adresse) {
            errors.adresse = "L'adresse est requise";
            isValid = false;
        }

        errors.telephone = '';
        if (!formData.telephone) {
            errors.telephone = "Le téléphone est requis";
            isValid = false;
        }

        errors.email = '';
        if (!formData.email) {
            errors.email = "L'email est requis";
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = "Le format de l'email est invalide";
            isValid = false;
        }

        errors.secteurs = '';
        if (!formData.secteurs || formData.secteurs.length === 0) {
            errors.secteurs = "Au moins un secteur est requis";
            isValid = false;
        }

        errors.date_creation = '';
        if (!formData.date_creation) {
            errors.date_creation = "La date de création est requise";
            isValid = false;
        }

        return isValid;
    }

    async function onSubmit() {
        if (!validate()) { return; }
        loading.value = true;
        errorMessage.value = '';
        try {
            // Utiliser FormData pour supporter l'upload de photo
            const formToUpload = new FormData();

            if (formData.id) {
                formToUpload.append('id', String(formData.id));
            }
            formToUpload.append('nom', formData.nom);
            formToUpload.append('rccm', formData.rccm);
            formToUpload.append('adresse', formData.adresse);
            formToUpload.append('telephone', formData.telephone);
            formToUpload.append('email', formData.email);
            formToUpload.append('pays_id', String(formData.pays_id));
            formToUpload.append('date_creation', formData.date_creation);
            formData.secteurs.forEach((secteur, index) => {
                formToUpload.append(`secteurs[${index}]`, secteur);
            });
            if (formData.logo) {
                formToUpload.append('logo', formData.logo);
            }
            const response = await axios.post(`/backoffice/collectivites/save_collectivite`, formToUpload);
            const savedCollectivite = response.data.collectivite;
            toastStore.success('Collectivité enregistré !');
            
            setTimeout(() => {
                window.location.href = `/app/collectivites/manage/${savedCollectivite.id}/gerant`;
            }, 100);
        } catch(error: any) {
            errorMessage.value = error.response?.data?.message || error.message || "Une erreur est survenue";
            console.error(error);
            loading.value = false;
        }
    }
</script>
<template>
    <section class="s20_mobile px-4">
        <div class="widget px-7 py-7">
            
            <div class="s10_mobile container-full-2"> 
                <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                    <img src="/public/icons/icon-info-perso.svg" class="size-l" alt="" />
                    Informations
                </h2>
            </div> 
            <div class="clear-both"></div>
            
            <div class="s10_mobile container-full-2"> 
                <div class="form-field-floating lg reactive active my-5">
                    <input type="text" class="form-input" name="nom" id="nom" v-model="formData.nom" placeholder="" />
                    <label for="nom">Désignation de la collectivité <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors['nom']" class="text-red">{{ errors['nom'] }}</span>

                <div class="form-field-floating lg reactive active my-5">
                    <input type="text" class="form-input" name="rccm" id="rccm" v-model="formData.rccm" placeholder="" :readonly="collectivite?.verrouille" />
                    <label for="rccm">Numéro du registre de commerce <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors['rccm']" class="text-red">{{ errors['rccm'] }}</span>

                <div class="form-field-floating active my-4">
                    <select class="form-input-select" name="pays_id" id="pays_id" v-model="formData.pays_id">
                        <option :value="null" selected>Sélectionnez un pays</option>
                        <option :value="pays.id" v-for="(pays, index) in startupFormDatas.pays" :key="'pays_' + index">
                            {{ pays.nom }}
                        </option>
                    </select>
                    <label for="pays_id">Pays <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors['pays_id']" class="text-red">{{ errors['pays_id'] }}</span>

                <div class="form-field-floating lg reactive my-5">
                    <input type="text" class="form-input" name="adresse" id="adresse" v-model="formData.adresse" placeholder="" />
                    <label for="adresse">Adresse de l'entreprise <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors['adresse']" class="text-red">{{ errors['adresse'] }}</span>

                <div class="form-field-split active my-5">
                    <custom-phone-input v-model="formData.telephone" id="telephone" classesNames="half" :readonly="collectivite?.verrouille" />
                    <hr class="hr" />
                    <div class="form-field-floating lg reactive half">
                        <input type="email" class="form-input" name="email" id="email" v-model="formData.email" placeholder="" :readonly="collectivite?.verrouille" />
                        <label for="email">Adresse Email <b class="color-danger">*</b></label>
                    </div>
                </div>
                <span v-if="!!errors['telephone']" class="text-red">{{ errors['telephone'] }}</span>
                <span v-if="!!errors['email']" class="text-red">{{ errors['email'] }}</span>
                
                <div class="form-field-floating lg reactive my-5">
                    <input type="date" class="form-input" name="date_creation" id="date_creation" v-model="formData.date_creation" placeholder="" />
                    <label for="date_creation">Date de création</label>
                </div>
                <span v-if="!!errors['date_creation']" class="text-red">{{ errors['date_creation'] }}</span>
            </div>  
            
            <div class="s10_mobile container-full-2">
                <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                    <img src="/public/icons/icon-info-collectivite.svg" class="size-l" alt="" />
                    Logo
                </h2>

                <div class="logo-uploader">
                    <div class="logo-preview">
                        <img v-if="logoPreview" :src="logoPreview" alt="Aperçu" />
                        <img v-else-if="existingLogo" :src="`${BACKEND_URL}/assets/collectivites/${existingLogo}`" alt="Logo actuel" />
                        <span v-else class="logo-placeholder">Aucun logo</span>
                    </div>
                    <div class="logo-actions">
                        <label for="logo-input" class="btn lg white2 active" style="cursor: pointer;">
                            <img src="/icons/icon-upload.svg" class="btn-icon" alt="" />
                            <span>{{ existingLogo || formData.logo ? 'Remplacer le logo' : 'Choisir un logo' }}</span>
                        </label>
                        <input id="logo-input" type="file" accept="image/*" style="display: none;" @change="onLogoSelected" />
                        <button v-if="formData.logo" type="button" class="btn lg white2" @click="clearLogo">
                            Annuler la sélection
                        </button>
                        <p class="text-sm text-grey-500 mt-2">PNG, JPG, SVG · max 5 Mo</p>
                    </div>
                </div>
            </div>

            <div class="s10_mobile container-full-2">
                <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                    <img src="/public/icons/icon-secteur-activite.svg" class="size-l" alt="" />
                    Secteur d'activité
                </h2>
                
                <div class="grid2 grid-row-gap-0">
                    <div v-for="secteur in secteurs" :key="secteur.id" class="card-check lg single">
                        <label class="form-check-label" :for="`secteur_${secteur.id}`">
                            <div class="card-check-text">
                                <input class="form-check-input left" type="checkbox" name="secteur[]" :id="`secteur_${secteur.id}`" :value="secteur.value" v-model="formData.secteurs" />
                                <h6 class="font-semibold mt-1 mb-2 align-right">{{ secteur.label }}</h6>
                            </div>  
                        </label>
                    </div>
                </div>
                <span v-if="!!errors['secteurs']" class="text-red">{{ errors['secteurs'] }}</span>
            </div>

            <!-- Actions -->
            <div class="s10_mobile container-full-2">&nbsp;</div>
            <div class="s10_mobile container-full-2">
                <span v-if="!!errorMessage" class="text-red">{{ errorMessage }}</span>
                <p class="text-xs text-grey-500 mb-5"><b class="color-danger text-xs">*</b> Les champs marqués d'un astérisque sont obligatoires</p>
                <div class="flex gap-2">
                    <custom-button type="button" :classesList="'btn lg w-100'" :loading="loading" @click="onSubmit()">
                        <img src="/icons/icon-enregistrer.svg" class="btn-icon" alt="" />
                        <span>Valider</span>
                    </custom-button>
                </div>
            </div>
            <div class="clear-both h-25"></div>
        </div>
    </section>
</template>
<style lang="css" scoped>

.logo-uploader {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
}

.logo-preview {
    width: 140px;
    height: 140px;
    border-radius: 20px;
    overflow: hidden;
    background-color: rgba(0, 0, 0, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
}

.logo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.logo-placeholder {
    color: rgba(0, 0, 0, 0.4);
    font-size: 0.85rem;
}

.logo-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}
</style>